/**
 * Shared input validation for user-controllable values.
 *
 * The DB is already safe from SQL injection (Drizzle parameterizes every query),
 * so these bounds are NOT about that. They exist for the other risks free-form
 * input carries: length abuse (DB / UI / email bloat) and control / bidi /
 * zero-width characters that enable log-injection, header-injection, and display
 * spoofing. Apply at every input boundary — createServerFn `.validator()`s, route
 * search params, and the auth hooks.
 */
import { z } from 'zod'

import { PublicError } from '@/lib/errors'

/**
 * Reject C0/C1 control chars, zero-width + bidirectional format chars, and the
 * BOM. Printable text only — a display name has no business carrying any of
 * these, and they enable log/header injection and display spoofing. Done by
 * codepoint (not a regex) so the source carries no invisible characters.
 */
function noUnsafeChars(s: string): boolean {
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0
    if (
      c <= 0x1f || // C0 controls (incl. NUL, tab, newline)
      (c >= 0x7f && c <= 0x9f) || // DEL + C1 controls
      (c >= 0x200b && c <= 0x200f) || // zero-width chars + LTR/RTL marks
      (c >= 0x202a && c <= 0x202e) || // bidi embeddings / overrides
      (c >= 0x2066 && c <= 0x2069) || // bidi isolates
      c === 0xfeff // BOM / zero-width no-break space
    ) {
      return false
    }
  }
  return true
}
const UNSAFE_MSG = 'Must not contain control or invisible characters.'

/**
 * Same charset rule as `noUnsafeChars`, but newline / carriage-return / tab are
 * allowed — for genuinely multi-line input (a support message). Everything the
 * single-line rule blocks for log/header-injection and display-spoofing reasons
 * stays blocked; only the three whitespace controls a paragraph legitimately
 * contains are let through.
 */
function noUnsafeCharsMultiline(s: string): boolean {
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0
    if (c === 0x09 || c === 0x0a || c === 0x0d) continue
    if (
      c <= 0x1f ||
      (c >= 0x7f && c <= 0x9f) ||
      (c >= 0x200b && c <= 0x200f) ||
      (c >= 0x202a && c <= 0x202e) ||
      (c >= 0x2066 && c <= 0x2069) ||
      c === 0xfeff
    ) {
      return false
    }
  }
  return true
}

/** Bounded, trimmed, control-char-free text. */
export const boundedText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${max} characters or fewer.`)
    .refine(noUnsafeChars, UNSAFE_MSG)

/** Required, trimmed, control-char-free text with a custom max length. */
export const requiredText = (max: number) =>
  z
    .string()
    .trim()
    .min(1, 'Required.')
    .max(max, `Must be ${max} characters or fewer.`)
    .refine(noUnsafeChars, UNSAFE_MSG)

/**
 * Required, trimmed, length-capped MULTI-LINE text (support message bodies).
 * Newlines survive; the rest of the unsafe-character rule still applies.
 */
export const requiredMultilineText = (max: number) =>
  z
    .string()
    .trim()
    .min(1, 'Required.')
    .max(max, `Must be ${max} characters or fewer.`)
    .refine(noUnsafeCharsMultiline, UNSAFE_MSG)

/** Required display name for a person or organization. */
export const displayName = requiredText(100)

/** Optional free-text label (e.g. a credential label). Empty becomes null. */
export const optionalLabel = z
  .preprocess(
    (v) => (v == null ? '' : v),
    z
      .string()
      .trim()
      .max(60, 'Label must be 60 characters or fewer.')
      .refine(noUnsafeChars, UNSAFE_MSG),
  )
  .transform((s) => s || null)

/** Optional bounded text that keeps empty as "" (not null) — for fields whose
 * handler already treats "" as "unset". Trimmed, charset-checked, length-capped. */
export const boundedOptionalString = (max: number) =>
  z.preprocess((v) => (v == null ? '' : v), boundedText(max))

/** Optional bounded note / description (admin ledger notes, offline payments). */
export const optionalNote = (max: number) =>
  z
    .preprocess(
      (v) => (v == null ? '' : v),
      z
        .string()
        .trim()
        .max(max, `Must be ${max} characters or fewer.`)
        .refine(noUnsafeChars, UNSAFE_MSG),
    )
    .transform((s) => s || null)

/** A URL-safe slug (org slug, plan slug). */
export const slug = z
  .string()
  .trim()
  .min(1, 'Required.')
  .max(64, 'Must be 64 characters or fewer.')
  .regex(/^[a-z0-9-]+$/, 'Use lowercase letters, digits, and hyphens only.')

/** An opaque row id referenced from the UI (nanoid- or uuid-shaped). */
export const id = z
  .string()
  .trim()
  .min(1, 'Required.')
  .max(64, 'Invalid id.')
  .regex(/^[A-Za-z0-9_-]+$/, 'Invalid id.')

/** An email address (bounded; Better Auth owns the auth-flow copy). */
export const email = z
  .string()
  .trim()
  .min(3, 'Enter a valid email address.')
  .max(254, 'Enter a valid email address.')
  .pipe(z.email('Enter a valid email address.'))

/**
 * Parse `data` with `schema`, mapping any validation failure to a user-facing
 * `PublicError` (shown verbatim, not sanitized). Use inside a createServerFn
 * `.validator()`.
 */
export function parseInput<T>(schema: z.ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data)
  if (!result.success) {
    throw new PublicError(result.error.issues[0]?.message ?? 'Invalid input.')
  }
  return result.data
}
